import React from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { useTimeModal } from '../../../Components/TimeModal/TimeModalContext';
import { formatDate, formatDateTime, formatOverflow, formatNull } from '../../../Utils/GlobalUtils';

import Loading from '../../../Components/Loading';
import LoadingLines from '../../../Components/LoadingLines';
import WarningPanel from '../../../Components/WarningPanel';
import ViewFileById from '../../ViewFileById';

import AddNewDocumentToApplication from '../../AddNewDocumentToApplication';

import styles from '../../../styles/DynamicModal.module.css';

import ButtonWithConfirmation from '../../ButtonWithConfirmation';

import FilterAMApplications from '../../FilterComponents/FilterAMApplications';
import DismissibleBanner from '../../DismissibleBanner';

import headerPanel from '../../../styles/HeaderPanel.module.css';

import IconContainer from '../../../Components/IconContainer';
import { ReactComponent as ReloadIcon } from '../../../icons/reload.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { ReactComponent as FunnelIcon } from '../../../icons/funnel-bold.svg';
import { ReactComponent as FunnelFillIcon } from '../../../icons/funnel-fill.svg';

export default function PendingApplications({ userId, isFromAccount }) {
    const { openTimeModal } = useTimeModal();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [cases, setCases] = React.useState([]);
    const [fioData, setFioData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [reload, setReload] = React.useState(true);
    const [warning, setWarning] = React.useState('');

    const [limitForView, setLimitForView] = React.useState(20);
    const [lastUpdateTime, setLastUpdateTime] = React.useState('');

    const [access, setAccess] = React.useState(false);
    React.useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get('/api/get-my-user-role', {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            const hasAccess = response.data.role === 'АппаратСРО';
            setAccess(hasAccess);
        }).catch(err => {
            console.error(`Ошибка получения данных: ${err}`);
        })
    }, []);

    const DEFAULT_FILTERS = {
        numbers: [],
    };

    const STORAGE_KEY = 'amapplications_filters';

    const [filters, setFilters] = React.useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
        } catch {
            return DEFAULT_FILTERS;
        }
    });

    React.useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    const hasActiveFilters = Object.entries(filters).some(([key, value]) => 
        Array.isArray(value) ? value.length > 0 : Boolean(value)
    );

    React.useEffect(() => {
        if (!userId) return;

        const token = localStorage.getItem('token');
        setLoading(true);

        if (isFromAccount) {
            axios.get(`/api/get-important-account-data?userId=${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setFioData(response.data);
            }).catch((error) => {
                console.error('Ошибка при получении данных пользователя:', error);
            })
        }

        if (!hasActiveFilters) {
            axios.get(`/api/get-user-pending-applications`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    user_id: userId,
                    limitForView
                }
            }).then((response) => {
                setWarning('');
                setCases(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее');
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        } else {
            axios.post(`/api/get-user-pending-applications-filtered`, filters, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    user_id: userId,
                    limitForView
                }
            }).then((response) => {
                setWarning('');
                setCases(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее');
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        }
    }, [reload, userId, hasActiveFilters]);

    const changeIsVisible = (application_id) => {
        axios.put(`/api/change-is-visible-application?application_id=${application_id}`)
        .then(() => {
            openTimeModal('Заявка перемещена в корзину', 'success');
        })
        .catch((error) => {
            console.error(error);
            openTimeModal('Не удалось переместить заявку в корзину', 'error');
        })
        .finally(() => {
            setReload(true);
        });
    };

    const [isNewDocumentToApplication, setIsNewDocumentToApplication] = React.useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = React.useState(null);
    const [selectedCaseId, setSelectedCaseId] = React.useState(null);

    const [isFilter, setIsFilter] = React.useState(false);
    const [fieldFilter, setFieldFilter] = React.useState(null);

    React.useEffect(() => {
        if (window.location.hash === '#filter') {
            setIsFilter(true);
        }
    }, []);

    return (
    <>
        {isNewDocumentToApplication && <AddNewDocumentToApplication applicationId={selectedApplicationId} caseId={selectedCaseId} isFirst={false} closeModal={() => {setIsNewDocumentToApplication(false)}} onSuccess={() => setReload(true)} />}
        {isFilter && <FilterAMApplications field={fieldFilter} filters={filters} setFilters={setFilters} DEFAULT_FILTERS={DEFAULT_FILTERS} userId={userId} closeModal={() => setIsFilter(false)} />}

        <div className={headerPanel.panel}>
            {/* <div className={headerPanel.searchBtn} onClick={() => setIsSearch(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M21.71,20.29,18,16.61A9,9,0,1,0,16.61,18l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,20.29ZM11,18a7,7,0,1,1,7-7A7,7,0,0,1,11,18Z" /></svg>
                Поиск...
            </div> */}
            <span></span>
            <button
                type='button'
                className='button miniButton'
                style={{width: 'fit-content'}}
                onClick={() => setSearchParams({ section: 'trash-pending-applications' })}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000000" viewBox="0 0 256 256"><path d="M216,48H180V36A28,28,0,0,0,152,8H104A28,28,0,0,0,76,36V48H40a12,12,0,0,0,0,24h4V208a20,20,0,0,0,20,20H192a20,20,0,0,0,20-20V72h4a12,12,0,0,0,0-24ZM100,36a4,4,0,0,1,4-4h48a4,4,0,0,1,4,4V48H100Zm88,168H68V72H188ZM116,104v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Zm48,0v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Z"></path></svg>
                Корзина
            </button>
        </div>

        {!access &&
        <div className='horizontalContainer'>
            <div className='boxPage'>
                <div className='boxHeader'>
                    <h3>Информация</h3>
                </div>
                <div className='boxMain' style={{fontSize: '14px'}}>
                    <div>
                        Данная таблица отображает заявки, на которые вы уже претендуете и по которым ожидается решение СРО. 
                        <br />
                        Чтобы посмотреть все предложения по заявкам, направленным вам СРО, перейдите в <Link className='aWithLink' to="/proposed-cases">"Витрину заявок"</Link>
                        <br />
                        Чтобы посмотреть ваши активные заявки (по которым СРО уже вас прикрепила) во <Link className='aWithLink' to="/applications?section=active-application-cases">вкладку "Активные заявки"</Link>
                    </div>
                </div>
            </div>
        </div>}

        {!access &&
        <DismissibleBanner 
            id="new-feauture-filters-for-am-applications-page" 
            headline='Новая функция'
            text={
                <span
                    style={{display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '3px'}}
                >
                    <h3 style={{fontSize: '15px', fontWeight: '500'}}>Фильтр по заявкам</h3>
                    Теперь у Вас есть возможность задавать фильтр по номеру заявки для более точного поиска среди всего списка. Для установки фильтра наведите указатель мыши на шапку таблицы и нажмите кнопку фильтра.
                </span>
            }
        />}

        <div className='pageWrapper'>
            <div className='tableContainer'>
                <div className='tableHeaderWrapper'>
                    <div className='tableStatus'>
                        <div>
                            <h3>{isFromAccount && (loading ? 'Загрузка...' : `Заявки управляющего (${fioData.lastname} ${fioData.firstname} ${fioData.surname})`)}</h3>
                            {hasActiveFilters && 
                                <span className='horizontalContainer'>
                                    <p>Применены фильтры</p>
                                    <p 
                                        className='aWithLink'
                                        onClick={() => {setFilters(DEFAULT_FILTERS); setReload(true)}}
                                    >
                                        Сбросить
                                    </p>
                                </span>
                            }
                        </div>
                        <a className='lastUpdateWrapper'>
                            Последнее обновление таблицы: {lastUpdateTime || '--:--'} 
                            {loading ? 
                                <Loading type='inline' /> 
                            : 
                                <IconContainer Icon={ReloadIcon} onClick={() => setReload(true)} size = '14' color = 'black' className = '' />
                            }
                        </a>
                    </div>
                    <div className='tableHeader' style={{gridTemplateColumns: '9fr 14fr 13fr 13fr 17fr 30fr 4fr'}}>
                        <div>
                            <p>Номер</p>
                            <IconContainer 
                                Icon={filters.numbers.length > 0 ? FunnelFillIcon : FunnelIcon} 
                                onClick={() => {
                                    setIsFilter(true);
                                    setFieldFilter('numbers');
                                }} 
                                size = '15' 
                                color = 'black' 
                                className = {`
                                    ${'filterHeaderIcon'} 
                                    ${filters.numbers.length > 0 && 'showedFilterHeaderIcon'}
                                `}
                            />
                        </div>
                        <div>Процедура / Должник</div>
                        <div>Дата рассылки предложения</div>
                        <div>Статус заявки</div>
                        <div>Количество претендующих</div>
                        <div>Файлы</div>
                        <div></div>
                    </div>
                </div>
                {(cases.length===0 && loading) ? <LoadingLines /> 
                : warning ? <WarningPanel text={warning} inTable={true} buttonText='Обновить' buttonAction={() => setReload(true)} />
                : cases.map((caseItem) =>
                    <div 
                        className='tableRow nonActive' 
                        key={caseItem.id} 
                        style={{gridTemplateColumns: '9fr 14fr 13fr 13fr 17fr 30fr 4fr'}}
                    >
                        <div>
                            {caseItem.status === 'Ваша кандидатура выбрана СРО' 
                            ? <p className='aWithLink' onClick={() => navigate(`/case/${caseItem.case_id}`, { state: { from: location.pathname + location.search + location.hash }})}>{formatNull(caseItem.number)}</p>
                            : 
                            <p>{formatNull(caseItem.number)}</p>}
                            {caseItem.case_link && <a className='aWithLink' href={caseItem.case_link}>Ссылка</a>}
                        </div>
                        <div>
                            <p className='grayText'>{formatNull(caseItem.procedure_name)}</p> 
                            <span className='horizontalLine'></span>
                            {caseItem.debtors ?
                            <p>
                                {formatOverflow(caseItem.debtors.map((e) => e.name).join(', '))}
                            </p>
                            :
                            formatNull(caseItem.debtors)
                            }
                        </div>
                        <div>
                            <p>{caseItem.mailing_status==='Письма не отправлены' ? formatNull() : formatDate(caseItem.date_mailing)}</p>
                            <p>(Согласия принимаются по {formatDate(caseItem.date_consent_deadline)})</p>
                        </div>
                        <div>
                            <p className={caseItem.status=='Ваша кандидатура выбрана СРО' ? 'colorCell green' : caseItem.status=='Ожидание' ? 'colorCell blue'  : ''}>{formatNull(caseItem.status)}</p>
                            {caseItem.status === 'Ваша кандидатура выбрана СРО' &&
                                <Link 
                                    className='aWithLink' 
                                    to={`/case/${caseItem.case_id}`}
                                >
                                    Перейти к карточке дела
                                </Link>
                            }
                        </div>
                        <div>{caseItem.status=='Ожидание' ? caseItem.count_pretenders : <p className='description'>Кандидатура уже выбрана</p>}</div>
                        <div>
                            {caseItem.files.length!==0 ?
                            <span
                                className={styles.timeline}
                            >
                            {caseItem.files.map((file, index) => (
                                <span
                                    key={index}
                                    className={styles.timelineItem}
                                    style={{maxWidth: 'none'}}
                                >
                                    <div
                                    >
                                        <p>
                                        {file.type=='appointmentAMAgreement' && 'Согласие на назначение'}
                                        {file.type=='appointmentAMRefusal' && 'Отзыв согласия на назначение'}
                                        </p>
                                        <p className='description'>{formatDateTime(file.created_at)}</p>
                                    </div>
                                    <ViewFileById fileId={file.file_id} />
                                </span>
                            ))}
                            </span>
                            :
                            <p className='description'>Файлы в карточке</p>}
                            {caseItem.status=='Ожидание' &&
                            <button 
                                type='button' 
                                className='miniButton' 
                                style={{width: '100%'}}
                                onClick={() => {setIsNewDocumentToApplication(true); setSelectedApplicationId(caseItem.id); setSelectedCaseId(caseItem.case_id)}}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" height="18" width="18" viewBox="0 0 18 18"><title>plus</title><g stroke-width="1.5" fill="none" stroke="#212121" class="nc-icon-wrapper"><line x1="9" y1="3.25" x2="9" y2="14.75" stroke-linecap="round" stroke-linejoin="round" stroke="#212121"></line><line x1="3.25" y1="9" x2="14.75" y2="9" stroke-linecap="round" stroke-linejoin="round"></line></g></svg>
                                Прикрепить новый документ
                            </button>}
                            {/* <p className='description'>По кнопке можно прикрепить другое согласие на назначение (если ранее была допущена ошибка) или отзыв согласия</p> */}
                        </div>
                        <div
                            style={{alignItems: 'end'}}
                        >
                            <ButtonWithConfirmation 
                                entryFunc={() => {
                                    if (caseItem.files.length!==0) {
                                        changeIsVisible(caseItem.id)
                                    } else {
                                        openTimeModal('Эту заявку нельзя убрать из списка', 'warning')
                                    }
                                }}
                                Icon={TrashIcon} 
                                IconSize={17}
                                buttonName='Переместить в корзину' 
                                color='red' 
                            />
                        </div>
                    </div>
                )}
                {!warning && cases.length > 0 && (cases.length < cases[0].total_count) && (
                    <div className='tableLoadMoreRow' style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                        <div></div>
                        <div>
                            <button
                                type='button'
                                className='button'
                                style={{ width: 'fit-content' }}
                                onClick={() => {setLimitForView(prev => prev + 50); setReload(true)}}
                                disabled={loading}
                            >
                                {loading ? <Loading type='inline' /> : 'Загрузить ещё'}
                            </button>
                        </div>
                        <div></div>
                    </div>
                )}
            </div>
        </div>
    </>
    )
}
